import { type HTMLAttributes, type ReactNode } from 'react';

import { cx } from '../lib/cx';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  children: ReactNode;
}

/** The 12px card surface — the base of every panel, chart card and table. */
export function Card({ className, children, ...rest }: CardProps): React.JSX.Element {
  return (
    <div className={cx('ds-card', className)} {...rest}>
      {children}
    </div>
  );
}

export interface CardHeaderProps {
  title: ReactNode;
  /** Optional right-aligned meta text. */
  meta?: ReactNode;
  /** Optional trailing action (e.g. a button), also right-aligned. */
  action?: ReactNode;
  /** Optional `?` help affordance content, rendered as a focusable tooltip elsewhere. */
  children?: ReactNode;
  /** Heading level for the title. Defaults to h2. */
  as?: 'h2' | 'h3';
}

export function CardHeader({ title, meta, action, children, as: As = 'h2' }: CardHeaderProps): React.JSX.Element {
  return (
    <div className="ds-card__header">
      <As className="ds-card__title">{title}</As>
      {children}
      {meta !== undefined ? <span className="ds-card__meta">{meta}</span> : null}
      {action !== undefined ? <span className="ds-card__meta">{action}</span> : null}
    </div>
  );
}

export function CardBody({ className, children, ...rest }: CardProps): React.JSX.Element {
  return (
    <div className={cx('ds-card__body', className)} {...rest}>
      {children}
    </div>
  );
}
